import {
  CalendarMonth as CalendarIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import {
  POReportParams,
  POReportRecord,
} from "../../../@types/DRO/AllReportsResponse";
import { GetAllVendorsListApiResponse } from "../../../@types/DRO/GetAllVendorsResponse";
import { GetAllWarehouseApiResponse } from "../../../@types/DRO/GetAllWarehouseResponse";
import { GetAllModelTypeArray } from "../../../@types/DRO/GetPartsListResponse";
import { AllReportsApi } from "../../../api/AllReportsDownload/AllReportsApi";
import { AllPartApi } from "../../../api/GetParts/GetAllPartsApi";
import { GetAllVendorsListApi } from "../../../api/GetVendorsList/GetAllVendorsListApi";
import { GetAllWarehouseListApi } from "../../../api/GetWarehouseList/GetAllWarehouseListApi";

const POReport: React.FC = () => {
  // State for data and pagination
  const [data, setData] = useState<POReportRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Calculate default dates (current date and 30 days before)
  const getDefaultDates = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    return {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };
  };

  // State for filters
  const [filters, setFilters] = useState<POReportParams>({
    vendor_name: "",
    product_id: undefined,
    product_name: "",
    po_no: undefined,
    location: undefined,
    status: "",
    start_date: getDefaultDates().startDate,
    end_date: getDefaultDates().endDate,
    page: 1,
    page_size: 50,
  });

  // State for dropdowns
  const [vendors, setVendors] = useState<
    GetAllVendorsListApiResponse["data"]["data"]
  >([]);
  const [warehouses, setWarehouses] = useState<
    GetAllWarehouseApiResponse["data"]["data"]
  >([]);
  const [modelTypes, setModelTypes] = useState<GetAllModelTypeArray[]>([]);

  // Selected values
  const [selectedVendor, setSelectedVendor] = useState<
    GetAllVendorsListApiResponse["data"]["data"][0] | null
  >(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<
    GetAllWarehouseApiResponse["data"]["data"][0] | null
  >(null);
  const [selectedOEMType, setSelectedOEMType] =
    useState<GetAllModelTypeArray | null>(null);

  // Pagination for dropdowns
  const [vendorsCurrentPage, setVendorsCurrentPage] = useState(0);
  const [warehousesCurrentPage, setWarehousesCurrentPage] = useState(0);
  const [vendorsSearchQuery, setVendorsSearchQuery] = useState("");
  const [warehousesSearchQuery, setWarehousesSearchQuery] = useState("");
  const [hasMoreVendors, setHasMoreVendors] = useState(true);
  const [hasMoreWarehouses, setHasMoreWarehouses] = useState(true);
  const rowsPerPage = 10;

  const fetchVendors = useCallback(async () => {
    try {
      const response = await GetAllVendorsListApi.api.getAllVendorsList(
        vendorsCurrentPage,
        rowsPerPage,
        vendorsSearchQuery
      );
      setVendors((prev) =>
        vendorsCurrentPage === 0
          ? response.data.data
          : [...prev, ...response.data.data]
      );
      setHasMoreVendors(response.data.data.length === rowsPerPage);
    } catch (error) {
      console.error("Error fetching vendors", error);
    }
  }, [vendorsCurrentPage, vendorsSearchQuery]);

  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await GetAllWarehouseListApi.api.getAllWarehouseList(
        warehousesCurrentPage,
        rowsPerPage,
        warehousesSearchQuery
      );
      setWarehouses((prev) =>
        warehousesCurrentPage === 0
          ? response.data.data
          : [...prev, ...response.data.data]
      );
      setHasMoreWarehouses(response.data.data.length === rowsPerPage);
    } catch (error) {
      console.error("Error fetching warehouses", error);
    }
  }, [warehousesCurrentPage, warehousesSearchQuery]);

  const fetchModelTypes = useCallback(async () => {
    try {
      const response = await AllPartApi.api.getModelTypeAll();
      setModelTypes(response.data);
    } catch (error) {
      console.error("Error fetching model types", error);
    }
  }, []);

  // Handle scroll for infinite loading
  const handleScroll = (
    event: React.SyntheticEvent,
    type: "vendors" | "warehouses"
  ) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;

    if (listboxNode.scrollHeight - position <= 1) {
      switch (type) {
        case "vendors":
          if (hasMoreVendors) {
            setVendorsCurrentPage((prev) => prev + 1);
          }
          break;
        case "warehouses":
          if (hasMoreWarehouses) {
            setWarehousesCurrentPage((prev) => prev + 1);
          }
          break;
      }
    }
  };

  // Fetch data function
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: POReportParams = {
        ...filters,
        vendor_name: selectedVendor?.name || filters.vendor_name,
        location: selectedWarehouse?.id,
        oem_type_id: selectedOEMType?.id,
        page: currentPage,
        page_size: pageSize,
      };

      // Remove empty string values
      Object.keys(params).forEach((key) => {
        const k = key as keyof POReportParams;
        if (params[k] === "" || params[k] === undefined) {
          delete params[k];
        }
      });

      const response = await AllReportsApi.api.getPOReport(params);
      if (response.success) {
        setData(response.records);
        setTotalPages(response.total_pages);
      }
    } catch (err) {
      setError("Failed to fetch PO report. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [
    filters,
    currentPage,
    pageSize,
    selectedVendor,
    selectedWarehouse,
    selectedOEMType,
  ]);

  // Initial fetch for dropdowns
  useEffect(() => {
    fetchModelTypes();
  }, [fetchModelTypes]);

  useEffect(() => {
    const debouncedFetchVendors = debounce(fetchVendors, 500);
    debouncedFetchVendors();
    return () => debouncedFetchVendors.cancel();
  }, [fetchVendors, vendorsCurrentPage]);

  useEffect(() => {
    const debouncedFetchWarehouses = debounce(fetchWarehouses, 500);
    debouncedFetchWarehouses();
    return () => debouncedFetchWarehouses.cancel();
  }, [fetchWarehouses, warehousesCurrentPage]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle numeric input with validation
  const handleNumericInputChange =
    (field: "product_id" | "po_no") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      // Allow only numbers and empty string
      if (value === "" || /^[0-9]+$/.test(value)) {
        setFilters({
          ...filters,
          [field]: value === "" ? undefined : parseInt(value, 10),
        });
        setCurrentPage(1);
      }
    };

  // Handle filter changes
  const handleFilterChange =
    (field: keyof POReportParams) =>
    (event: React.ChangeEvent<HTMLInputElement | { value: unknown }>) => {
      setFilters({
        ...filters,
        [field]: event.target.value,
      });
      setCurrentPage(1);
    };

  // Handle search button click
  const handleSearch = () => {
    fetchData();
  };

  // Handle clear filters
  const handleClearFilters = () => {
    const defaultDates = getDefaultDates();
    setFilters({
      vendor_name: "",
      product_id: undefined,
      product_name: "",
      po_no: undefined,
      location: undefined,
      status: "",
      start_date: defaultDates.startDate,
      end_date: defaultDates.endDate,
    });
    setSelectedVendor(null);
    setSelectedWarehouse(null);
    setSelectedOEMType(null);
    setCurrentPage(1);
  };

  // Handle download
  const handleDownload = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        vendor_name: selectedVendor?.name || filters.vendor_name,
        location: selectedWarehouse?.id,
        oem_type_id: selectedOEMType?.id,
      };
      delete params.page;
      delete params.page_size;
      await AllReportsApi.api.downloadPOReport(params);
    } catch (err) {
      setError("Failed to download report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(value);
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case "CREATED":
        return { bgcolor: "#e3f2fd", color: "#1976d2" };
      case "COMPLETED":
        return { bgcolor: "#e8f5e9", color: "#2e7d32" };
      case "CLOSED":
        return { bgcolor: "#fff8e1", color: "#ff8f00" };
      case "APPROVED":
        return { bgcolor: "#f3e5f5", color: "#8e24aa" };
      case "CANCELLED":
        return { bgcolor: "#ffebee", color: "#d32f2f" };
      default:
        return { bgcolor: "#f5f5f5", color: "#666" };
    }
  };

  return (
    <Box sx={{ width: "100%", minHeight: "100vh" }}>
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Card sx={{ mb: 3, boxShadow: 0, border: "1px solid #e0e0e0" }}>
          <CardContent>
            <Typography variant="h4" sx={{ fontWeight: 600, color: "#000" }}>
              Purchase Order (PO) Report
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", mt: 1 }}>
              View and export purchase order reports with vendor and product
              details
            </Typography>
          </CardContent>
        </Card>

        {/* Filters Section */}
        <Card sx={{ mb: 3, boxShadow: 0, border: "1px solid #e0e0e0" }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
              Filter Options
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
                gap: 2,
                mb: 3,
              }}>
              <Autocomplete
                fullWidth
                size="small"
                options={vendors}
                getOptionLabel={(option) => option.name}
                value={selectedVendor}
                onChange={(event, newValue) => {
                  setSelectedVendor(newValue);
                  if (newValue) {
                    setFilters({
                      ...filters,
                      vendor_id: newValue.id,
                    });
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  setVendorsCurrentPage(0);
                  setVendorsSearchQuery(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vendor"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: "#000",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#000",
                      },
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: (e) => handleScroll(e, "vendors"),
                  style: { maxHeight: 200, overflow: "auto" },
                }}
              />
              <TextField
                label="Reference Code"
                value={filters.product_id || ""}
                onChange={handleFilterChange("product_id")}
                size="small"
                fullWidth
                inputProps={{
                  inputMode: "text",
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <TextField
                label="Part Name"
                value={filters.product_name}
                onChange={handleFilterChange("product_name")}
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <TextField
                label="PO Id"
                value={filters.po_no || ""}
                onChange={handleNumericInputChange("po_no")}
                size="small"
                fullWidth
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <Autocomplete
                fullWidth
                size="small"
                options={warehouses}
                getOptionLabel={(option) => option.name}
                value={selectedWarehouse}
                onChange={(event, newValue) => {
                  setSelectedWarehouse(newValue);
                }}
                onInputChange={(event, newInputValue) => {
                  setWarehousesCurrentPage(0);
                  setWarehousesSearchQuery(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Warehouse"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: "#000",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#000",
                      },
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: (e) => handleScroll(e, "warehouses"),
                  style: { maxHeight: 200, overflow: "auto" },
                }}
              />
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status || ""}
                  onChange={(event) => {
                    setFilters({
                      ...filters,
                      status: event.target.value as string,
                    });
                    setCurrentPage(1);
                  }}
                  label="Status"
                  sx={{
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#000",
                    },
                  }}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="CREATED">CREATED</MenuItem>
                  <MenuItem value="COMPLETED">COMPLETED</MenuItem>
                  <MenuItem value="CLOSED">CLOSED</MenuItem>
                  <MenuItem value="APPROVED">APPROVED</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Start Date"
                type="date"
                value={filters.start_date}
                onChange={handleFilterChange("start_date")}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <CalendarIcon sx={{ mr: 1, color: "#666" }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <TextField
                label="End Date"
                type="date"
                value={filters.end_date}
                onChange={handleFilterChange("end_date")}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <CalendarIcon sx={{ mr: 1, color: "#666" }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                sx={{
                  borderColor: "#000",
                  color: "#000",
                  "&:hover": {
                    borderColor: "#000",
                    bgcolor: "#f5f5f5",
                  },
                }}>
                Clear Filters
              </Button>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                sx={{
                  bgcolor: "#000",
                  color: "#fff",
                  "&:hover": {
                    bgcolor: "#333",
                  },
                }}>
                Search
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                disabled={loading}
                sx={{
                  bgcolor: "#fff",
                  color: "#000",
                  border: "2px solid #000",
                  "&:hover": {
                    bgcolor: "#f5f5f5",
                  },
                  "&:disabled": {
                    bgcolor: "#e0e0e0",
                    color: "#999",
                  },
                }}>
                Download Excel
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, border: "1px solid #ff5252" }}
            onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Data Table */}
        <Card sx={{ boxShadow: 0, border: "1px solid #e0e0e0" }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                  <CircularProgress sx={{ color: "#000" }} />
                </Box>
              ) : (
                <>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Reference Code
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Vendor Name
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>PO ID</TableCell>

                        <TableCell sx={{ fontWeight: 600 }}>
                          Part Name
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Quantity Received
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Cost Price
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>GST</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>HSN</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Warehouse
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Label</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Created At
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Close Date
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={15} align="center" sx={{ py: 5 }}>
                            <Typography color="text.secondary">
                              No records found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.map((row, index) => (
                          <TableRow
                            key={`${row.vendor_id}-${row.po_id}-${index}`}
                            sx={{
                              "&:hover": { bgcolor: "#fafafa" },
                              "&:last-child td": { border: 0 },
                            }}>
                            <TableCell>{row.reference_code || "-"}</TableCell>
                            <TableCell>
                              <Tooltip title={row.vendor_name}>
                                <Typography
                                  sx={{
                                    maxWidth: 200,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}>
                                  {row.vendor_name}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>{row.po_id}</TableCell>

                            <TableCell>
                              <Tooltip title={row.product_name || "-"}>
                                <Typography
                                  sx={{
                                    maxWidth: 200,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}>
                                  {row.product_name || "-"}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>{row.quantity_of_vendor}</TableCell>
                            <TableCell>
                              {row.quantity_received ?? "-"}
                            </TableCell>
                            <TableCell>
                              {formatCurrency(row.cost_price)}
                            </TableCell>
                            <TableCell>{row.gst}</TableCell>
                            <TableCell>{row.hsn}</TableCell>
                            <TableCell>{row.location}</TableCell>
                            <TableCell>
                              <Chip
                                label={row.status}
                                size="small"
                                sx={getStatusColor(row.status)}
                              />
                            </TableCell>
                            <TableCell>{row.label || "-"}</TableCell>
                            <TableCell>{row.created_at}</TableCell>
                            <TableCell>{row.po_close_date}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 2,
                      borderTop: "1px solid #e0e0e0",
                      bgcolor: "#fafafa",
                    }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography variant="body2">Rows per page:</Typography>
                      <Select
                        value={pageSize}
                        onChange={(e) => handlePageSizeChange(e as any)}
                        size="small"
                        sx={{
                          minWidth: 80,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e0e0e0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000",
                          },
                        }}>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={200}>200</MenuItem>
                      </Select>
                    </Box>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      showFirstButton
                      showLastButton
                      sx={{
                        "& .MuiPaginationItem-root": {
                          "&.Mui-selected": {
                            bgcolor: "#000",
                            color: "#fff",
                            "&:hover": {
                              bgcolor: "#333",
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </>
              )}
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default POReport;
